import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from "../config/fbConfig";
import SalesPDFGenerator from "./SalesPDFGenerator";
import generateReceiptPDF from "./ReceiptGenerator";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import Loader from "./Loader";
import SuccessModal from "./SuccessModal";
import Modal from "react-modal";
import Logo from "../images/shop_logo.png";

Modal.setAppElement("#root");

const SalesHistory = () => {
  const { userData } = useUserAuth();
  const { search } = useLocation();
  const navigate = useNavigate();

  const [groupedSalesData, setGroupedSalesData] = useState([]);
  const [filteredSalesData, setFilteredSalesData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [totalSales, setTotalSales] = useState(0);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewingSale, setViewingSale] = useState(null);

  // Parse URL params
  const params = new URLSearchParams(search);
  const selectedDate = params.get("date");   // e.g., "2025-11-09"
  const selectedMonth = params.get("month"); // e.g., "2025-11"

  const formatDate = (date) => {
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).replace(/ /g, "_");
  };

  const fetchSalesData = useCallback(async () => {
    if (!userData) return;

    const allowedRoles = ["Supreme", "Admin", "Cashier"];
    if (!allowedRoles.includes(userData.status)) {
      navigate("/");
      return;
    }

    setLoading(true);
    let startDate, endDate, periodLabel, daysInPeriod;

    try {
      if (selectedDate) {
        // DAILY SALES: Specific date selected
        const [year, month, day] = selectedDate.split("-").map(Number);
        startDate = new Date(year, month - 1, day, 0, 0, 0, 0);
        endDate = new Date(year, month - 1, day, 23, 59, 59, 999);
        periodLabel = new Date(startDate).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
        daysInPeriod = 1;
      } else if (selectedMonth) {
        // MONTHLY SALES: Full month
        const [year, month] = selectedMonth.split("-").map(Number);
        startDate = new Date(year, month - 1, 1, 0, 0, 0, 0);
        endDate = new Date(year, month, 0, 23, 59, 59, 999); // Last day of month
        periodLabel = new Date(year, month - 1).toLocaleString("en-US", {
          month: "long",
          year: "numeric",
        });
        daysInPeriod = endDate.getDate();
      } else {
        // No valid param → redirect
        setError("No date or month selected.");
        setShowModal(true);
        setLoading(false);
        return;
      }

      const salesQuery = query(
        collection(db, "sales"),
        where("timestamp", ">=", startDate),
        where("timestamp", "<=", endDate),
        orderBy("timestamp", "desc")
      );

      const snapshot = await getDocs(salesQuery);
      const sales = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Group by date
      const groupedByDate = sales.reduce((acc, sale) => {
        const saleDate = sale.timestamp?.toDate?.() || new Date();
        const dateKey = formatDate(saleDate);
        const dateLabel = saleDate.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        });

        if (!acc[dateKey]) {
          acc[dateKey] = { date: dateLabel, sales: [] };
        }
        acc[dateKey].sales.push(sale);
        return acc;
      }, {});

      const groupedArray = Object.values(groupedByDate).sort((a, b) => {
        return new Date(b.date) - new Date(a.date);
      });

      const total = sales.reduce((sum, sale) => sum + (sale.grandTotal || 0), 0);

      setGroupedSalesData(groupedArray);
      setFilteredSalesData(groupedArray);
      setTotalSales(total);
      window.history.replaceState({}, "", `/sales-history?${selectedDate ? 'date' : 'month'}=${selectedDate || selectedMonth}`);
    } catch (err) {
      setError("Failed to load sales: " + err.message);
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  }, [userData, selectedDate, selectedMonth, navigate]);

  useEffect(() => {
    fetchSalesData();
  }, [fetchSalesData]);

  // Search filter
  useEffect(() => {
    if (!searchQuery) {
      setFilteredSalesData(groupedSalesData);
      return;
    }

    const filtered = groupedSalesData
      .map((group) => ({
        ...group,
        sales: group.sales.filter((sale) =>
          `${sale.cashierName || ""} ${sale.customerName || ""}`
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        ),
      }))
      .filter((group) => group.sales.length > 0);

    setFilteredSalesData(filtered);
  }, [searchQuery, groupedSalesData]);

  const handlePrintReceipt = (sale) => {
    try {
      const cart = sale.products.map((p) => ({
        ...p,
        id: Math.random().toString(36).substr(2, 9),
        price: p.total / p.quantity,
        subtotal: p.total,
      }));

      const receiptNo = String(Math.floor(Math.random() * 1000000)).padStart(6, "0");
      const doc = generateReceiptPDF(
        cart,
        { email: sale.cashierName },
        sale.cashierName?.split(" ")[0] || "Cashier",
        sale.cashierName?.split(" ").slice(1).join(" ") || "",
        sale.customerName || "Customer",
        sale.customerPhone || "",
        sale.timestamp
      );
      doc.save(`receipt_${receiptNo}_${formatDate(new Date())}.pdf`);
    } catch (err) {
      setError("Print failed: " + err.message);
      setShowModal(true);
    }
  };

  if (loading) return <Loader text="LOADING" />;
  if (!userData) return <Loader text="READING" />;

  const periodLabel = selectedDate
    ? new Date(selectedDate).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : selectedMonth
    ? new Date(selectedMonth + "-01").toLocaleString("en-US", { month: "long", year: "numeric" })
    : "Sales History";

  const daysInPeriod = selectedDate ? 1 : selectedMonth ? new Date(selectedMonth.split("-")[0], selectedMonth.split("-")[1], 0).getDate() : 1;

  return (
    <>
      {["Supreme", "Admin", "Cashier"].includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <div className="column align-center justify-center">
              <img src={Logo} className="middle-logo" alt="logo" />
            </div>
            <Link to="#" className="home-link" onClick={(e) => { e.preventDefault(); fetchSalesData(); }}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items column align-center scroll-fixed">
            <h2 className="manage-page-heading sales-history-heading caps size4">
              Sales History ({periodLabel})
            </h2>

            <div className="stat-content-by-class row align-center space-between">
              <p className="bold stat-heading-d">
                Total Sales - ₦ {totalSales.toLocaleString()}
              </p>
              <div className="row justify-center align-center gap-15 add-buttons">
                <SalesPDFGenerator
                  sales={groupedSalesData.flatMap((g) => g.sales)}
                  days={daysInPeriod}
                  buttonText={`Print ${selectedDate ? "Daily" : "Monthly"} Report`}
                />
              </div>
            </div>

            <input
              type="text"
              className="input-field1"
              placeholder="Search by cashier or customer name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {filteredSalesData.length === 0 ? (
                  <p className="loading-text">No sales found for this period</p>
                ) : (
                  filteredSalesData.map((group, i) => (
                    <div key={i} className="date-group">
                      <p className="bold size5 date-heading">{group.date}</p>
                      {group.sales.map((sale, j) => (
                        <div key={j} className="class-item class-item-sales-history column align-start">
                          <span className="size5">
                            Sale by {sale.cashierName || "Unknown"} ({sale.cashierPhone || "N/A"})
                          </span>
                          <span className="male-female-stat">
                            {sale.products?.length || 0} Product{sale.products?.length !== 1 ? "s" : ""} -{" "}
                            <span className="bold">₦ {sale.grandTotal?.toLocaleString() || 0}</span>
                          </span>
                          <div className="row align-center space-between time-and-buttons">
                            <span className="size55 italics">
                              {sale.timestamp?.toDate?.().toLocaleTimeString() || "N/A"}
                            </span>
                            <div className="row gap-15">
                              <button className="list-btn" onClick={() => { setViewingSale(sale); setIsViewModalOpen(true); }}>
                                <i className="fas fa-eye"></i>
                              </button>
                              <button className="list-btn delete-btn" onClick={() => handlePrintReceipt(sale)}>
                                <i className="fas fa-print"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* View Sale Modal */}
          <Modal isOpen={isViewModalOpen} onRequestClose={() => setIsViewModalOpen(false)} className="custom-modal" overlayClassName="custom-modal-overlay">
            <h2>Sale Details</h2>
            {viewingSale && (
              <div className="column modal-scroll">
                <p><strong>Cashier:</strong> {viewingSale.cashierName || "N/A"}</p>
                <p><strong>Customer:</strong> {viewingSale.customerName || "Walk-in"}</p>
                <p><strong>Time:</strong> {viewingSale.timestamp?.toDate?.().toLocaleString()}</p>
                <p><strong>Products:</strong></p>
                <ul style={{ paddingLeft: 20 }}>
                  {viewingSale.products?.map((p, i) => (
                    <li key={i}>
                      {p.name} × {p.quantity} @ ₦{p.unitPrice?.toLocaleString()} = ₦{p.total.toLocaleString()}
                    </li>
                  ))}
                </ul>
                <p><strong>Grand Total:</strong> ₦ {viewingSale.grandTotal?.toLocaleString()}</p>
                <button className="login-button caps" onClick={() => setIsViewModalOpen(false)}>Close</button>
              </div>
            )}
          </Modal>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">
          Access denied.
        </p>
      )}

      {showModal && (
        <SuccessModal
          icon="xmark"
          iconColor="red"
          message={error}
          onClose={() => { setError(""); setShowModal(false); }}
        />
      )}
    </>
  );
};

export default SalesHistory;